import React, { useState, useEffect, useRef, useMemo } from 'react';
import SearchFilter from './SearchFilter';
import TrainersFilter from './TrainersFilter';
import FitFilter from './FitFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'react-bootstrap';
import { clearMightyFitFilter, removeMightyFitFilter, selectMightyFitFilters, selectSelectedFilters } from '../core/filterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { loadWorkouts } from '../core/workoutSlice';

export default function FilterPage() {
    const dispatch = useDispatch();
    const [isFitFilterOpen, setIsFitFilterOpen] = useState(false);
    const [isTrainerFilterOpen, setIsTrainerFilterOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const filterRef = useRef<HTMLDivElement | null>(null);
    const mightyFitFilters = useSelector(selectMightyFitFilters);
    const selectedFilters = useSelector(selectSelectedFilters);
    const didMountRef = useRef(false);
    const filters = useMemo(() => {
        return mightyFitFilters || {
            difficulty_level: [],
            training_types: [],
            equipments: [],
            trainers: [],
        };
    }, [mightyFitFilters]);

    const {
        difficulty_level = [],
        training_types = [],
        equipments = [],
        trainers = []
    } = filters;

    useEffect(() => {
        if (didMountRef.current) {
            if (selectedFilters.length <= 0) {
                dispatch(loadWorkouts('mighty_fits?page=1'));
            } else {
                handleFilterAPIResponse(selectedFilters);
            }
        } else {
            didMountRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters]);

    const handleIsFilterOpen = () => {
        if (isTrainerFilterOpen) {
            setIsTrainerFilterOpen(false);
        }
        setIsFitFilterOpen(!isFitFilterOpen);
    };

    const handleIsTrainerOpen = () => {
        if (isFitFilterOpen) {
            setIsFitFilterOpen(false);
        }
        setIsTrainerFilterOpen(!isTrainerFilterOpen);
    };

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
                setIsFitFilterOpen(false);
                setIsTrainerFilterOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    const handleRemovetags = (tagValue: string) => {
        dispatch(removeMightyFitFilter(tagValue));
    };

    const handleClearAllTags = () => {
        dispatch(clearMightyFitFilter());
    };

    const handleFilterAPIResponse = async (selected: any) => {
        const normalizedSearchFilter = selected
            .filter((v: any) => typeof v === "string" && v.startsWith("search_text="))
            .map((v: string) => v.replace("search_text=", "").trim());

        const normalizedStringFilter = selected
            .filter((v: any) => isNaN(Number(v)) && !(typeof v === "string" && v.startsWith("search_text=")))
            .map(String);

        const normalizedNumberFilter = selected
            .filter((v: any) => !isNaN(Number(v)))
            .map((v: any) => Number(v));

        const filteredData = {
            equipments: equipments.filter((item: any) =>
                normalizedStringFilter.includes(`equipment:${item.slug}`)
            ),
            difficulty_level: difficulty_level.filter((item: any) =>
                normalizedStringFilter.includes(`difficulty:${item.level}`)
            ),
            training_type: training_types.filter((item: any) =>
                normalizedStringFilter.includes(`training:${item.slug}`)
            ),
            trainer_id: trainers.filter((item: any) =>
                normalizedNumberFilter.includes(Number(item.id))
            ),
        };

        const queryParams: string[] = [];
        for (const [key, items] of Object.entries(filteredData)) {
            const values = items
                .map((item: any) => item.slug || item.level || item.id)
                .filter(Boolean)
                .join(',');

            if (values) {
                queryParams.push(`${key}=${values}`);
            }
        }

        if (normalizedSearchFilter.length > 0) {
            queryParams.push(`search_text=${normalizedSearchFilter[0]}`);
        }

        const url = `mighty_fits?page=1${queryParams.length ? `&${queryParams.join('&')}` : ''}`;
        await dispatch(loadWorkouts(url));
    };

    const tagFilters = useMemo(() => {
        const allItems = [
            ...difficulty_level.map((item: any) => ({
                value: `difficulty:${item.level}`,
                label: item.level?.toString(),
            })),
            ...training_types.map((item: any) => ({
                value: `training:${item.slug}`,
                label: item.name,
            })),
            ...equipments.map((item: any) => ({
                value: `equipment:${item.slug}`,
                label: item.name,
            })),
            ...trainers.map((item: any) => ({
                value: Number(item.id),
                label: item.trainer_name,
            })),
        ];

        return selectedFilters
            .filter((value: any) => !(typeof value === "string" && value.includes("search_text=")))
            .map((value: any) => {
                const matchedItem = allItems.find((item) => item.value === value);
                return {
                    value,
                    label: matchedItem?.label || value.replace(/.*:/, ''),
                };
            });
    }, [selectedFilters, difficulty_level, training_types, equipments, trainers]);

    return (
        <div className={`filter-part ${tagFilters.length > 0 ? 'pt-5 pb-4' : 'p-5'}`}>
            <div className="container">
                <div className="fp-wrapper" ref={filterRef}>
                    <div className="bottom-filter">
                        <div className="both-filter mt-2">
                            <button className={`${isTrainerFilterOpen ? 'isOpen ' : ''}trainers`} onClick={handleIsTrainerOpen}>
                                <div className="bf-title">
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className='user'>
                                        <path d="M12.75 15.75V14.25C12.75 13.4544 12.4339 12.6913 11.8713 12.1287C11.3087 11.5661 10.5456 11.25 9.75 11.25H3.75C2.95435 11.25 2.19129 11.5661 1.62868 12.1287C1.06607 12.6913 0.75 13.4544 0.75 14.25V15.75" stroke="#707070" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M6.75 8.25C8.40685 8.25 9.75 6.90685 9.75 5.25C9.75 3.59315 8.40685 2.25 6.75 2.25C5.09315 2.25 3.75 3.59315 3.75 5.25C3.75 6.90685 5.09315 8.25 6.75 8.25Z" stroke="#707070" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M17.25 15.75V14.25C17.2495 13.5853 17.0283 12.9396 16.621 12.4142C16.2138 11.8889 15.6436 11.5137 15 11.3475" stroke="#707070" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M12 2.3475C12.6453 2.51273 13.2173 2.88803 13.6257 3.41424C14.0342 3.94044 14.2559 4.58763 14.2559 5.25375C14.2559 5.91988 14.0342 6.56706 13.6257 7.09327C13.2173 7.61948 12.6453 7.99478 12 8.16" stroke="#707070" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className="bf-item text-dark">Trainers</span>
                                    <svg width="10" className='btnArrow' height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 1L5 5L9 1" stroke="#707070" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            </button>
                            <button className={`${isFitFilterOpen ? 'isOpen ' : ''}trainers`} onClick={handleIsFilterOpen}>
                                <div className="bf-title">
                                    <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" className='user'>
                                        <path d="M16 2.5H1L7 9.595V14.5L10 16V9.595L16 2.5Z" stroke="#2B2B2B" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className="bf-item text-dark">Filter</span>
                                    <svg width="10" className='btnArrow' height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 1L5 5L9 1" stroke="#707070" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            </button>
                        </div>
                        <SearchFilter
                            searchText={searchText}
                            setSearchText={setSearchText}
                        />
                    </div>
                    {isTrainerFilterOpen && <TrainersFilter />}
                    {isFitFilterOpen && <FitFilter />}
                    <div className="display-checkbox">
                        <div className="wrapcheck">
                            {tagFilters.length > 0 && (
                                tagFilters.map(({ value, label }: any, index: React.Key | null | undefined) => (
                                    <div className="checkmark" key={index}>
                                        <div className="selected-checkboxes">
                                            <p className="check-text">{label}</p>
                                        </div>
                                        <FontAwesomeIcon
                                            id={value}
                                            role="button"
                                            className="pe-auto"
                                            icon={faXmark}
                                            size={"xl" as SizeProp}
                                            onClick={() => handleRemovetags(value)}
                                        />
                                    </div>
                                ))
                            )}
                        </div>
                        {tagFilters.length > 0 && (
                            <Button variant="success" className="btn-clear" onClick={handleClearAllTags}>
                                Clear All
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}